/**
 * Per-section settings form (spec 42 R2, design 43 AD2).
 *
 * One editor per section type. Fields carry live character counters because
 * every cap is enforced server-side — a user who only discovers the limit on
 * Save has been wasted.
 */
import { Plus, Trash2 } from "lucide-react";
import { Input, Textarea, Button } from "@flint/ui";
import type { HomeSection, LinkTarget, SectionCta } from "@flint/api-contract/store-home";
import { SectionImageField } from "./section-image-field.tsx";
import { LabelledField } from "./labelled-field.tsx";

/** Counter turns amber near the cap and red at it (design 43 AD2). */
function Counter({ value, max }: { value: string; max: number }) {
  const n = value.length;
  const tone = n >= max ? "text-bad" : n >= max * 0.9 ? "text-warn" : "text-muted";
  return (
    <span aria-live="polite" className={`text-xs tabular-nums ${tone}`}>
      {n}/{max}
    </span>
  );
}

function TextField({
  label,
  value,
  max,
  onChange,
  placeholder,
  multiline,
}: {
  label: string;
  value: string;
  max: number;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
}) {
  const Control = multiline ? Textarea : Input;
  return (
    <LabelledField label={label} hint={<Counter value={value} max={max} />}>
      {(id) => (
        <Control
          id={id}
          value={value}
          maxLength={max}
          placeholder={placeholder}
          onChange={(e: { target: { value: string } }) => onChange(e.target.value)}
        />
      )}
    </LabelledField>
  );
}


/**
 * Link target editor (spec 42 R3). Kind first, then only the argument that
 * kind needs — so an external URL and a collection can't both be half-filled.
 * The https rule is stated up front rather than sprung on Save.
 */
function TargetField({
  label,
  value,
  collections,
  onChange,
  onClear,
}: {
  label: string;
  value: LinkTarget | undefined;
  collections: Array<{ slug: string; name: string }>;
  onChange: (t: LinkTarget) => void;
  onClear: () => void;
}) {
  const kind = value?.kind ?? "none";
  return (
    <div className="flex flex-col gap-2">
      <LabelledField label={label}>
        {(id) => (
        <select
          id={id}
          className="h-9 rounded-sm border border-line bg-paper px-2 text-sm"
          value={kind}
          onChange={(e) => {
            const k = e.target.value;
            if (k === "none") return onClear();
            if (k === "collection") {
              const first = collections[0]?.slug;
              return first ? onChange({ kind: "collection", slug: first }) : onClear();
            }
            if (k === "search") return onChange({ kind: "search" });
            if (k === "whatsapp") return onChange({ kind: "whatsapp" });
            return onChange({ kind: "url", href: "https://" });
          }}
        >
          <option value="none">No link</option>
          <option value="collection" disabled={collections.length === 0}>
            A collection
          </option>
          <option value="search">Search / all products</option>
          <option value="whatsapp">WhatsApp enquiry</option>
          <option value="url">An external link</option>
        </select>
        )}
      </LabelledField>

      {value?.kind === "collection" ? (
        <LabelledField label="Which collection">
{(id) => (
            <select id={id}
              className="h-9 rounded-sm border border-line bg-paper px-2 text-sm"
              value={value.slug}
              onChange={(e) => onChange({ kind: "collection", slug: e.target.value })}
            >
              {collections.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.name}
                </option>
              ))}
            </select>
)}
</LabelledField>
      ) : null}

      {value?.kind === "url" ? (
        <LabelledField label="Address" hint={<span className="text-xs text-muted">Must start with https://</span>}>
{(id) => (
            <Input id={id}
              value={value.href}
              placeholder="https://"
              onChange={(e: { target: { value: string } }) =>
                onChange({ kind: "url", href: e.target.value })
              }
            />
)}
</LabelledField>
      ) : null}

      {value?.kind === "whatsapp" ? (
        <p className="text-xs text-muted">
          Uses your store's contact number. If none is set, the button simply won't show.
        </p>
      ) : null}
    </div>
  );
}

/**
 * The up-to-two buttons on a hero slide (schema: `ctas`, max 2). The first
 * renders solid, the second outlined — same order the storefront paints them.
 * A button must link somewhere, so "No link" resets the target to Search
 * rather than leaving it empty; removing the button is the explicit control.
 */
function HeroSlideCtas({
  ctas,
  collections,
  onChange,
}: {
  ctas: SectionCta[];
  collections: Array<{ slug: string; name: string }>;
  onChange: (next: SectionCta[]) => void;
}) {
  const setAt = (k: number, next: SectionCta) =>
    onChange(ctas.map((c, j) => (j === k ? next : c)));

  return (
    <div className="mt-3 flex flex-col gap-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted">Buttons</p>
      {ctas.map((cta, k) => (
        <div key={k} className="flex flex-col gap-2 rounded-sm border border-line p-2.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-ink">
              {k === 0 ? "Primary (solid)" : "Secondary (outlined)"}
            </span>
            <Button
              type="button"
              intent="ghost"
              size="sm"
              onClick={() => onChange(ctas.filter((_, j) => j !== k))}
            >
              <Trash2 className="size-4" aria-hidden />
              Remove
            </Button>
          </div>
          <TextField
            label="Label"
            value={cta.label}
            max={24}
            onChange={(v) => setAt(k, { ...cta, label: v })}
          />
          <TargetField
            label="Links to"
            value={cta.target}
            collections={collections}
            onChange={(t) => setAt(k, { ...cta, target: t })}
            onClear={() => setAt(k, { ...cta, target: { kind: "search" } })}
          />
        </div>
      ))}
      {/* Schema caps the slide at two buttons — the control disables and says why. */}
      <Button
        type="button"
        intent="ghost"
        size="md"
        disabled={ctas.length >= 2}
        title={ctas.length >= 2 ? "A slide can have at most two buttons." : undefined}
        onClick={() =>
          onChange([
            ...ctas,
            { label: ctas.length === 0 ? "Browse catalog" : "Contact us", target: { kind: "search" } },
          ])
        }
      >
        <Plus className="size-4" aria-hidden />
        Add button
      </Button>
    </div>
  );
}

/**
 * Section types that own their own colour and so take no background scheme.
 *
 * Mirrors the renderer's `FULL_BLEED` + `SELF_STYLED` sets in
 * `home-sections.tsx`. The promo banner is here because it uses the theme
 * tokens inverted (dark card, light text): a band remaps exactly those two and
 * turns its text invisible, so the renderer ignores the scheme — and a control
 * the renderer ignores must not be offered.
 */
const NO_SCHEME = new Set(["hero_slider", "announcement_bar", "promo_banner"]);

const SCHEME_OPTIONS: Array<{ value: "default" | "muted" | "accent" | "dark"; label: string }> = [
  { value: "default", label: "None (page background)" },
  { value: "muted", label: "Muted surface" },
  { value: "accent", label: "Accent" },
  { value: "dark", label: "Dark" },
];

export function SectionForm({
  section,
  collections,
  onChange,
}: {
  section: HomeSection;
  collections: Array<{ slug: string; name: string }>;
  onChange: (next: HomeSection) => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <SectionBody section={section} collections={collections} onChange={onChange} />
      {NO_SCHEME.has(section.type) ? null : (
        <LabelledField label="Background">
          {(id) => (
            <select
              id={id}
              className="h-9 rounded-sm border border-line bg-paper px-2 text-sm"
              value={section.scheme ?? "default"}
              onChange={(e) => {
                const v = e.target.value as "default" | "muted" | "accent" | "dark";
                if (v === "default") {
                  // Omit the key rather than set it undefined (exactOptional).
                  const { scheme: _drop, ...rest } = section;
                  onChange(rest as HomeSection);
                } else {
                  onChange({ ...section, scheme: v });
                }
              }}
            >
              {SCHEME_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          )}
        </LabelledField>
      )}
    </div>
  );
}

function SectionBody({
  section,
  collections,
  onChange,
}: {
  section: HomeSection;
  collections: Array<{ slug: string; name: string }>;
  onChange: (next: HomeSection) => void;
}) {
  switch (section.type) {
    case "hero_slider":
      return (
        <div className="flex flex-col gap-4">
          {section.slides.map((slide, i) => (
            <div key={i} className="rounded-sm border border-line p-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">
                Slide {i + 1} of {section.slides.length}
              </p>
              <TextField
                label="Headline"
                value={slide.headline}
                max={60}
                onChange={(v) =>
                  onChange({
                    ...section,
                    slides: section.slides.map((s, j) => (j === i ? { ...s, headline: v } : s)),
                  })
                }
              />
              <TextField
                label="Subhead"
                value={slide.subhead ?? ""}
                max={120}
                onChange={(v) =>
                  onChange({
                    ...section,
                    slides: section.slides.map((s, j) =>
                      j === i ? { ...s, subhead: v || undefined } : s,
                    ),
                  })
                }
              />
              <SectionImageField
                label="Slide image"
                value={slide.imageUrl}
                hint="Wide images work best — around 1600×500. Without one the slide shows a textured band."
                onChange={(url) =>
                  onChange({
                    ...section,
                    slides: section.slides.map((s, j) => (j === i ? { ...s, imageUrl: url } : s)),
                  })
                }
              />
              <HeroSlideCtas
                ctas={slide.ctas}
                collections={collections}
                onChange={(next) =>
                  onChange({
                    ...section,
                    slides: section.slides.map((s, j) => (j === i ? { ...s, ctas: next } : s)),
                  })
                }
              />
              {section.slides.length > 1 ? (
                <Button
                  type="button"
                  intent="ghost"
                  size="md"
                  className="mt-2"
                  onClick={() =>
                    onChange({ ...section, slides: section.slides.filter((_, j) => j !== i) })
                  }
                >
                  <Trash2 className="size-4" aria-hidden />
                  Remove slide {i + 1}
                </Button>
              ) : null}
            </div>
          ))}
          {/* R2 caps the slider at 5 — the control disables and says why. */}
          <Button
            type="button"
            intent="ghost"
            size="md"
            disabled={section.slides.length >= 5}
            title={section.slides.length >= 5 ? "A slider can have at most 5 slides." : undefined}
            onClick={() =>
              onChange({
                ...section,
                slides: [...section.slides, { headline: "Your headline here", ctas: [] }],
              })
            }
          >
            <Plus className="size-4" aria-hidden />
            Add slide
          </Button>
          <LabelledField label="Autoplay">
{(id) => (
              <select id={id}
                className="h-9 rounded-sm border border-line bg-paper px-2 text-sm"
                value={section.autoplayMs}
                onChange={(e) =>
                  onChange({ ...section, autoplayMs: Number(e.target.value) as 0 | 4000 | 6000 | 8000 })
                }
              >
                <option value={0}>Off</option>
                <option value={4000}>Every 4 seconds</option>
                <option value={6000}>Every 6 seconds</option>
                <option value={8000}>Every 8 seconds</option>
              </select>
)}
</LabelledField>
          <p className="text-xs text-muted">
            Autoplay never runs for buyers who ask their device to reduce motion, and a pause
            control always shows.
          </p>
        </div>
      );

    case "product_grid":
      return (
        <div className="flex flex-col gap-4">
          <TextField
            label="Heading"
            value={section.heading}
            max={40}
            onChange={(v) => onChange({ ...section, heading: v })}
          />
          <TextField
            label="Subheading"
            value={section.subheading ?? ""}
            max={120}
            onChange={(v) => onChange({ ...section, subheading: v || undefined })}
          />
          <LabelledField label="Show products from">
{(id) => (
              <select id={id}
                className="h-9 rounded-sm border border-line bg-paper px-2 text-sm"
                value={section.source.kind === "collection" ? section.source.slug : section.source.kind}
                onChange={(e) => {
                  const v = e.target.value;
                  onChange({
                    ...section,
                    source:
                      v === "newest" || v === "uncollected"
                        ? { kind: v }
                        : { kind: "collection", slug: v },
                  });
                }}
              >
                <option value="newest">Newest published</option>
                <option value="uncollected">Not in any collection</option>
                {collections.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.name}
                  </option>
                ))}
              </select>
)}
</LabelledField>
          <LabelledField label="How many">
{(id) => (
              <select id={id}
                className="h-9 rounded-sm border border-line bg-paper px-2 text-sm"
                value={section.limit}
                onChange={(e) => onChange({ ...section, limit: Number(e.target.value) as 4 | 8 })}
              >
                <option value={4}>4 products</option>
                <option value={8}>8 products</option>
              </select>
)}
</LabelledField>
        </div>
      );

    case "collection_rail":
      return (
        <LabelledField label="Which collections">
{(id) => (
            <select id={id}
              className="h-9 rounded-sm border border-line bg-paper px-2 text-sm"
              value={section.source.kind === "all-visible" ? "all-visible" : section.source.slug}
              onChange={(e) => {
                const v = e.target.value;
                onChange({
                  ...section,
                  source: v === "all-visible" ? { kind: "all-visible" } : { kind: "collection", slug: v },
                });
              }}
            >
              <option value="all-visible">One rail per collection</option>
              {collections.map((c) => (
                <option key={c.slug} value={c.slug}>
                  Only {c.name}
                </option>
              ))}
            </select>
)}
</LabelledField>
      );

    case "stats_strip": {
      const items = section.items ?? [];
      return (
        <div className="flex flex-col gap-4">
          <TextField
            label="Heading (optional)"
            value={section.heading ?? ""}
            max={40}
            onChange={(v) => onChange({ ...section, heading: v || undefined })}
          />
          {items.length === 0 ? (
            <p className="text-sm text-muted">
              With no stats added, this shows the details from your store profile — established
              year, team size, monthly capacity and city. Add your own below to state them
              directly instead.
            </p>
          ) : (
            items.map((item, i) => (
              <div key={i} className="rounded-sm border border-line p-3">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">
                  Stat {i + 1} of {items.length}
                </p>
                <TextField
                  label="Figure"
                  value={item.value}
                  max={24}
                  placeholder="40,000/mo"
                  onChange={(v) =>
                    onChange({
                      ...section,
                      items: items.map((it, j) => (j === i ? { ...it, value: v } : it)),
                    })
                  }
                />
                <TextField
                  label="Caption"
                  value={item.label}
                  max={24}
                  placeholder="Monthly capacity"
                  onChange={(v) =>
                    onChange({
                      ...section,
                      items: items.map((it, j) => (j === i ? { ...it, label: v } : it)),
                    })
                  }
                />
                <Button
                  type="button"
                  intent="ghost"
                  size="md"
                  className="mt-2"
                  onClick={() => {
                    const next = items.filter((_, j) => j !== i);
                    onChange({ ...section, items: next.length ? next : undefined });
                  }}
                >
                  <Trash2 className="size-4" aria-hidden />
                  Remove stat {i + 1}
                </Button>
              </div>
            ))
          )}
          <Button
            type="button"
            intent="ghost"
            size="md"
            disabled={items.length >= 4}
            title={items.length >= 4 ? "Up to 4 stats." : undefined}
            onClick={() =>
              onChange({ ...section, items: [...items, { value: "2012", label: "Established" }] })
            }
          >
            <Plus className="size-4" aria-hidden />
            Add stat
          </Button>
        </div>
      );
    }

    case "about_block":
      return (
        <div className="flex flex-col gap-4">
          <TextField
            label="Heading (optional)"
            value={section.heading ?? ""}
            max={60}
            placeholder="About your store"
            onChange={(v) => onChange({ ...section, heading: v || undefined })}
          />
          <TextField
            label="Body (optional)"
            value={section.body ?? ""}
            max={1200}
            multiline
            onChange={(v) => onChange({ ...section, body: v || undefined })}
          />
          <p className="text-xs text-muted">
            Leave the body blank to show the About text from your store profile instead.
          </p>
        </div>
      );

    case "announcement_bar":
      return (
        <div className="flex flex-col gap-4">
          <TextField
            label="Message"
            value={section.text}
            max={120}
            placeholder="Free delivery on orders over ₹50,000"
            onChange={(v) => onChange({ ...section, text: v })}
          />
          <LabelledField label="Style">
{(id) => (
              <select id={id}
                className="h-9 rounded-sm border border-line bg-paper px-2 text-sm"
                value={section.tone}
                onChange={(e) => onChange({ ...section, tone: e.target.value as "info" | "promo" })}
              >
                <option value="info">Information</option>
                <option value="promo">Promotion</option>
              </select>
)}
</LabelledField>
          <LabelledField label="Colour">
{(id) => (
              <select id={id}
                className="h-9 rounded-sm border border-line bg-paper px-2 text-sm"
                value={section.scheme ?? "default"}
                onChange={(e) => {
                  const v = e.target.value as "default" | "muted" | "accent" | "dark";
                  if (v === "default") {
                    // Omit the key rather than set it undefined (exactOptional).
                    const { scheme: _drop, ...rest } = section;
                    onChange(rest as HomeSection);
                  } else {
                    onChange({ ...section, scheme: v });
                  }
                }}
              >
                <option value="default">Default (matches style)</option>
                <option value="muted">Muted surface</option>
                <option value="accent">Accent</option>
                <option value="dark">Dark</option>
              </select>
)}
</LabelledField>
          <p className="text-xs text-muted">
            Colours just this bar — it doesn&apos;t follow the page theme.
          </p>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={section.dismissible}
              onChange={(e) => onChange({ ...section, dismissible: e.target.checked })}
            />
            Buyers can dismiss it
          </label>
          <TargetField
            label="Links to"
            value={section.target}
            collections={collections}
            onChange={(t) => onChange({ ...section, target: t })}
            onClear={() => {
              const { target: _drop, ...rest } = section;
              onChange(rest as HomeSection);
            }}
          />
          <p className="text-xs text-muted">
            The bar always shows at the very top of your home page, whatever order it sits in here.
          </p>
        </div>
      );

    case "category_grid":
      return (
        <div className="flex flex-col gap-4">
          <TextField
            label="Heading"
            value={section.heading}
            max={40}
            onChange={(v) => onChange({ ...section, heading: v })}
          />
          <TextField
            label="Subheading"
            value={section.subheading ?? ""}
            max={120}
            onChange={(v) => onChange({ ...section, subheading: v || undefined })}
          />
          <LabelledField label="Tile style">
{(id) => (
              <select id={id}
                className="h-9 rounded-sm border border-line bg-paper px-2 text-sm"
                value={section.style}
                onChange={(e) =>
                  onChange({ ...section, style: e.target.value as "cards" | "circles" })
                }
              >
                <option value="cards">Cards with images</option>
                <option value="circles">Small circles</option>
              </select>
)}
</LabelledField>
          <LabelledField label="How many">
{(id) => (
              <select id={id}
                className="h-9 rounded-sm border border-line bg-paper px-2 text-sm"
                value={section.limit}
                onChange={(e) => onChange({ ...section, limit: Number(e.target.value) })}
              >
                {[4, 6, 8, 10, 12].map((n) => (
                  <option key={n} value={n}>
                    {n} collections
                  </option>
                ))}
              </select>
)}
</LabelledField>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={section.showCounts}
              onChange={(e) => onChange({ ...section, showCounts: e.target.checked })}
            />
            Show how many products each holds
          </label>
        </div>
      );

    case "promo_banner":
      return (
        <div className="flex flex-col gap-4">
          <TextField
            label="Eyebrow"
            value={section.eyebrow ?? ""}
            max={32}
            placeholder="FOR RETAILERS"
            onChange={(v) => onChange({ ...section, eyebrow: v || undefined })}
          />
          <TextField
            label="Headline"
            value={section.headline}
            max={60}
            onChange={(v) => onChange({ ...section, headline: v })}
          />
          <TextField
            label="Body"
            value={section.body ?? ""}
            max={240}
            multiline
            onChange={(v) => onChange({ ...section, body: v || undefined })}
          />
          <SectionImageField
            label="Image"
            value={section.imageUrl}
            onChange={(url) => onChange({ ...section, imageUrl: url })}
          />
          <LabelledField label="Image side">
{(id) => (
              <select id={id}
                className="h-9 rounded-sm border border-line bg-paper px-2 text-sm"
                value={section.imageSide}
                onChange={(e) =>
                  onChange({ ...section, imageSide: e.target.value as "left" | "right" })
                }
              >
                <option value="right">Image on the right</option>
                <option value="left">Image on the left</option>
              </select>
)}
</LabelledField>
          <TargetField
            label="Button links to"
            value={section.cta?.target}
            collections={collections}
            onChange={(t) =>
              onChange({ ...section, cta: { label: section.cta?.label || "Learn more", target: t } })
            }
            onClear={() => {
              const { cta: _drop, ...rest } = section;
              onChange(rest as HomeSection);
            }}
          />
          {section.cta ? (
            <TextField
              label="Button label"
              value={section.cta.label}
              max={24}
              onChange={(v) => onChange({ ...section, cta: { ...section.cta!, label: v } })}
            />
          ) : null}
          <p className="text-xs text-muted">
            This banner is presentation only — it doesn't apply a discount.
          </p>
        </div>
      );

    case "rich_text":
      return (
        <div className="flex flex-col gap-4">
          <TextField
            label="Heading"
            value={section.heading}
            max={60}
            onChange={(v) => onChange({ ...section, heading: v })}
          />
          <TextField
            label="Body"
            value={section.body}
            max={2000}
            multiline
            onChange={(v) => onChange({ ...section, body: v })}
          />
          <LabelledField label="Alignment">
            {(id) => (
              <select
                id={id}
                className="h-9 rounded-sm border border-line bg-paper px-2 text-sm"
                value={section.align}
                onChange={(e) => onChange({ ...section, align: e.target.value as "left" | "center" })}
              >
                <option value="left">Left</option>
                <option value="center">Centered</option>
              </select>
            )}
          </LabelledField>
        </div>
      );

    case "image_text":
      return (
        <div className="flex flex-col gap-4">
          <TextField
            label="Heading"
            value={section.heading}
            max={60}
            onChange={(v) => onChange({ ...section, heading: v })}
          />
          <TextField
            label="Body"
            value={section.body ?? ""}
            max={500}
            multiline
            onChange={(v) => onChange({ ...section, body: v || undefined })}
          />
          <SectionImageField
            label="Image"
            value={section.imageUrl}
            onChange={(url) => onChange({ ...section, imageUrl: url })}
          />
          <LabelledField label="Image side">
            {(id) => (
              <select
                id={id}
                className="h-9 rounded-sm border border-line bg-paper px-2 text-sm"
                value={section.imageSide}
                onChange={(e) =>
                  onChange({ ...section, imageSide: e.target.value as "left" | "right" })
                }
              >
                <option value="right">Image on the right</option>
                <option value="left">Image on the left</option>
              </select>
            )}
          </LabelledField>
          <TargetField
            label="Button links to"
            value={section.cta?.target}
            collections={collections}
            onChange={(t) =>
              onChange({ ...section, cta: { label: section.cta?.label || "Learn more", target: t } })
            }
            onClear={() => {
              const { cta: _drop, ...rest } = section;
              onChange(rest as HomeSection);
            }}
          />
          {section.cta ? (
            <TextField
              label="Button label"
              value={section.cta.label}
              max={24}
              onChange={(v) => onChange({ ...section, cta: { ...section.cta!, label: v } })}
            />
          ) : null}
        </div>
      );

    case "testimonials":
      return (
        <div className="flex flex-col gap-4">
          <TextField
            label="Heading (optional)"
            value={section.heading ?? ""}
            max={40}
            onChange={(v) => onChange({ ...section, heading: v || undefined })}
          />
          {section.items.map((item, i) => (
            <div key={i} className="rounded-sm border border-line p-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">
                Quote {i + 1} of {section.items.length}
              </p>
              <TextField
                label="Quote"
                value={item.quote}
                max={280}
                multiline
                onChange={(v) =>
                  onChange({
                    ...section,
                    items: section.items.map((it, j) => (j === i ? { ...it, quote: v } : it)),
                  })
                }
              />
              <TextField
                label="Author"
                value={item.author}
                max={60}
                onChange={(v) =>
                  onChange({
                    ...section,
                    items: section.items.map((it, j) => (j === i ? { ...it, author: v } : it)),
                  })
                }
              />
              {section.items.length > 1 ? (
                <Button
                  type="button"
                  intent="ghost"
                  size="md"
                  className="mt-2"
                  onClick={() =>
                    onChange({ ...section, items: section.items.filter((_, j) => j !== i) })
                  }
                >
                  <Trash2 className="size-4" aria-hidden />
                  Remove quote {i + 1}
                </Button>
              ) : null}
            </div>
          ))}
          <Button
            type="button"
            intent="ghost"
            size="md"
            disabled={section.items.length >= 6}
            title={section.items.length >= 6 ? "Up to 6 quotes." : undefined}
            onClick={() =>
              onChange({
                ...section,
                items: [...section.items, { quote: "A great review", author: "Dealer name" }],
              })
            }
          >
            <Plus className="size-4" aria-hidden />
            Add quote
          </Button>
        </div>
      );

    case "faq":
      return (
        <div className="flex flex-col gap-4">
          <TextField
            label="Heading (optional)"
            value={section.heading ?? ""}
            max={40}
            onChange={(v) => onChange({ ...section, heading: v || undefined })}
          />
          {section.items.map((item, i) => (
            <div key={i} className="rounded-sm border border-line p-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">
                Question {i + 1} of {section.items.length}
              </p>
              <TextField
                label="Question"
                value={item.question}
                max={160}
                onChange={(v) =>
                  onChange({
                    ...section,
                    items: section.items.map((it, j) => (j === i ? { ...it, question: v } : it)),
                  })
                }
              />
              <TextField
                label="Answer"
                value={item.answer}
                max={600}
                multiline
                onChange={(v) =>
                  onChange({
                    ...section,
                    items: section.items.map((it, j) => (j === i ? { ...it, answer: v } : it)),
                  })
                }
              />
              {section.items.length > 1 ? (
                <Button
                  type="button"
                  intent="ghost"
                  size="md"
                  className="mt-2"
                  onClick={() =>
                    onChange({ ...section, items: section.items.filter((_, j) => j !== i) })
                  }
                >
                  <Trash2 className="size-4" aria-hidden />
                  Remove question {i + 1}
                </Button>
              ) : null}
            </div>
          ))}
          <Button
            type="button"
            intent="ghost"
            size="md"
            disabled={section.items.length >= 12}
            title={section.items.length >= 12 ? "Up to 12 questions." : undefined}
            onClick={() =>
              onChange({
                ...section,
                items: [...section.items, { question: "A question?", answer: "The answer." }],
              })
            }
          >
            <Plus className="size-4" aria-hidden />
            Add question
          </Button>
        </div>
      );

    case "logo_wall": {
      const items = section.items;
      return (
        <div className="flex flex-col gap-4">
          <TextField
            label="Heading (optional)"
            value={section.heading ?? ""}
            max={40}
            onChange={(v) => onChange({ ...section, heading: v || undefined })}
          />
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={section.grayscale}
              onChange={(e) => onChange({ ...section, grayscale: e.target.checked })}
            />
            Greyscale until hover
          </label>
          {items.map((it, i) => (
            <div key={i} className="rounded-sm border border-line p-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">
                Logo {i + 1} of {items.length}
              </p>
              <SectionImageField
                label="Logo image"
                value={it.imageUrl || undefined}
                onChange={(url) =>
                  onChange({
                    ...section,
                    items: items.map((x, j) => (j === i ? { ...x, imageUrl: url ?? "" } : x)),
                  })
                }
              />
              <TextField
                label="Alt text (optional)"
                value={it.alt ?? ""}
                max={60}
                onChange={(v) =>
                  onChange({
                    ...section,
                    items: items.map((x, j) => (j === i ? { ...x, alt: v || undefined } : x)),
                  })
                }
              />
              {items.length > 1 ? (
                <Button
                  type="button"
                  intent="ghost"
                  size="md"
                  className="mt-2"
                  onClick={() => onChange({ ...section, items: items.filter((_, j) => j !== i) })}
                >
                  <Trash2 className="size-4" aria-hidden />
                  Remove logo {i + 1}
                </Button>
              ) : null}
            </div>
          ))}
          <Button
            type="button"
            intent="ghost"
            size="md"
            disabled={items.length >= 12}
            title={items.length >= 12 ? "Up to 12 logos." : undefined}
            onClick={() => onChange({ ...section, items: [...items, { imageUrl: "" }] })}
          >
            <Plus className="size-4" aria-hidden />
            Add logo
          </Button>
        </div>
      );
    }

    case "gallery": {
      const images = section.images;
      return (
        <div className="flex flex-col gap-4">
          <TextField
            label="Heading (optional)"
            value={section.heading ?? ""}
            max={40}
            onChange={(v) => onChange({ ...section, heading: v || undefined })}
          />
          <LabelledField label="Columns">
            {(id) => (
              <select
                id={id}
                className="h-9 rounded-sm border border-line bg-paper px-2 text-sm"
                value={section.columns}
                onChange={(e) => onChange({ ...section, columns: Number(e.target.value) as 2 | 3 | 4 })}
              >
                <option value={2}>2 columns</option>
                <option value={3}>3 columns</option>
                <option value={4}>4 columns</option>
              </select>
            )}
          </LabelledField>
          {images.map((src, i) => (
            <div key={i} className="rounded-sm border border-line p-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">
                Image {i + 1} of {images.length}
              </p>
              <SectionImageField
                label="Image"
                value={src || undefined}
                onChange={(url) =>
                  onChange({
                    ...section,
                    images: images.map((x, j) => (j === i ? url ?? "" : x)),
                  })
                }
              />
              {images.length > 1 ? (
                <Button
                  type="button"
                  intent="ghost"
                  size="md"
                  className="mt-2"
                  onClick={() => onChange({ ...section, images: images.filter((_, j) => j !== i) })}
                >
                  <Trash2 className="size-4" aria-hidden />
                  Remove image {i + 1}
                </Button>
              ) : null}
            </div>
          ))}
          <Button
            type="button"
            intent="ghost"
            size="md"
            disabled={images.length >= 12}
            title={images.length >= 12 ? "Up to 12 images." : undefined}
            onClick={() => onChange({ ...section, images: [...images, ""] })}
          >
            <Plus className="size-4" aria-hidden />
            Add image
          </Button>
        </div>
      );
    }

    case "featured_product":
      return (
        <div className="flex flex-col gap-4">
          <TextField
            label="Eyebrow"
            value={section.eyebrow ?? ""}
            max={32}
            placeholder="FEATURED"
            onChange={(v) => onChange({ ...section, eyebrow: v || undefined })}
          />
          <TextField
            label="Headline"
            value={section.headline}
            max={60}
            onChange={(v) => onChange({ ...section, headline: v })}
          />
          <TextField
            label="Body"
            value={section.body ?? ""}
            max={300}
            multiline
            onChange={(v) => onChange({ ...section, body: v || undefined })}
          />
          <TextField
            label="Price line"
            value={section.priceLabel ?? ""}
            max={40}
            placeholder="From ₹499 / pc"
            onChange={(v) => onChange({ ...section, priceLabel: v || undefined })}
          />
          <SectionImageField
            label="Image"
            value={section.imageUrl}
            onChange={(url) => onChange({ ...section, imageUrl: url })}
          />
          <TargetField
            label="Button links to"
            value={section.cta?.target}
            collections={collections}
            onChange={(t) =>
              onChange({ ...section, cta: { label: section.cta?.label || "View product", target: t } })
            }
            onClear={() => {
              const { cta: _drop, ...rest } = section;
              onChange(rest as HomeSection);
            }}
          />
          {section.cta ? (
            <TextField
              label="Button label"
              value={section.cta.label}
              max={24}
              onChange={(v) => onChange({ ...section, cta: { ...section.cta!, label: v } })}
            />
          ) : null}
        </div>
      );

    case "contact":
      return (
        <div className="flex flex-col gap-4">
          <TextField
            label="Heading (optional)"
            value={section.heading ?? ""}
            max={40}
            onChange={(v) => onChange({ ...section, heading: v || undefined })}
          />
          <TextField
            label="Body"
            value={section.body ?? ""}
            max={300}
            multiline
            onChange={(v) => onChange({ ...section, body: v || undefined })}
          />
          <TextField
            label="Address"
            value={section.address ?? ""}
            max={200}
            multiline
            placeholder="12 Mill Road, Tiruppur 641604"
            onChange={(v) => onChange({ ...section, address: v || undefined })}
          />
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={section.showWhatsapp}
              onChange={(e) => onChange({ ...section, showWhatsapp: e.target.checked })}
            />
            Show a WhatsApp button (uses your store phone)
          </label>
        </div>
      );

    case "newsletter":
    case "lead_capture":
      return (
        <div className="flex flex-col gap-4">
          <TextField
            label="Heading"
            value={section.heading}
            max={60}
            onChange={(v) => onChange({ ...section, heading: v })}
          />
          <TextField
            label="Subtext"
            value={section.subtext ?? ""}
            max={160}
            multiline
            onChange={(v) => onChange({ ...section, subtext: v || undefined })}
          />
          <TextField
            label="Button label"
            value={section.buttonLabel ?? ""}
            max={24}
            placeholder={section.type === "newsletter" ? "Notify me" : "Send enquiry"}
            onChange={(v) => onChange({ ...section, buttonLabel: v || undefined })}
          />
          <p className="text-xs text-muted">
            Submissions open WhatsApp to your store number with the details filled in. Add a store
            phone number for it to work.
          </p>
        </div>
      );

    default:
      // Every known type has an editor above, so this is only reached by a
      // section saved by a newer build than the one running — the same
      // forward-compatibility case the renderer handles by skipping.
      return (
        <p className="text-sm text-muted">
          This section was set up in a newer version of Flint. It still shows on your storefront,
          and you can move, hide or remove it here — but editing its settings needs the newer
          version.
        </p>
      );
  }
}
